import { getMenuItems } from '@/actions/app-action';
import { AppConfig } from '@/config/app';
import { encryptWithAES } from '@/lib/auth';
import { MenuItem } from '@/types/api';
import { LoginCredential, LoginToken } from '@/types/frontend';
import { Department, Role, User } from '@/types/user';
import { del, get, post, put } from '@/utils/api-util';
import { message } from 'antd';
import { randomBytes } from 'crypto';

const fetchMockData = async () => {
    const response = await fetch('/mock/data/user.json');
    return response.json();
};

export const listMockUsers = async (): Promise<User[]> => {
    if (process.env.NODE_ENV !== 'development') {
        return [];
    }
    return (await fetchMockData()).mockUsers;
};

export async function listMenu(): Promise<MenuItem<unknown>[]> {
    return await getMenuItems();
}

export const authLogin = async (credentials: LoginCredential): Promise<{ user: User, token: LoginToken, pathPermissions: string[] }> => {
    if (!credentials.account || !credentials.password) {
        throw new Error('账号和密码不能为空');
    }
    // 结合当前时间戳和随机数生成一个随机的验证码，13 位时间戳 + 5 位随机数
    const randomCodeWithTime = `${new Date().getTime()}${randomBytes(8).toString('hex')}`;

    // 修改后的加密代码
    const encryptedPassword = encryptWithAES(credentials.password, randomCodeWithTime);

    const payload = {
        account: credentials.account,
        password: encryptedPassword
    };

    const result = (await post('/api/v1/auth/login', payload, undefined, {
        loadingMessage: '正在登录...',
        successMessage: '登录成功，欢迎回来！',
        headers: {
            [AppConfig.headers.requestId]: randomCodeWithTime
        }
    })).data

    return result as { user: User; token: LoginToken; pathPermissions: string[] };
};

export const authLoginWithToken = async (token: string): Promise<{ user: User, token: LoginToken, pathPermissions: string[] }> => {
    if (!token) {
        throw new Error('token不能为空');
    }
    const result = (await post('/api/v1/auth/login-with-token', { token }, undefined, {
        loadingMessage: '正在登录...',
        successMessage: '登录成功，欢迎回来！'
    })).data
    return result as { user: User; token: LoginToken; pathPermissions: string[] };
}

/**
 * 获取所有角色
 * @returns 角色列表
 * @version 1.0.0
 */
export const listAllRoles = async (): Promise<Role[]> => {
    return (await get('/api/v1/roles', { fetchAll: true }, {
        loadingMessage: '正在加载角色列表',
        successMessage: '角色列表加载成功'
    })).data.items;
}

/**
 * 获取所有可用角色
 * @returns 角色列表
 * @version 1.0.0
 */
export const listAllOnRoles = async (): Promise<Role[]> => {
    return (await get('/api/v1/roles', { fetchAll: true, status: 'ON' }, {
        loadingMessage: '正在加载角色列表',
        successMessage: '角色列表加载成功'
    })).data.items;
}

/**
 * 创建角色
 * @param data 角色数据
 * @returns 角色数据
 * @version 1.0.0
 */
export const createRole = async (data: any) => {
    return (await post('/api/v1/role', data, undefined, {
        loadingMessage: '正在创建角色',
        successMessage: '角色创建成功'
    })).data;
}

/**
 * 删除角色
 * @param id 角色ID
 * @returns 删除结果
 * @version 1.0.0
 */
export const deleteRole = async (id: number) => {
    return (await del(`/api/v1/role`, { id }, {
        loadingMessage: '正在删除角色',
        successMessage: '角色删除成功'
    })).data;
}

/**
 * 更新角色
 * @param data 角色数据
 * @returns 更新结果
 * @version 1.0.0
 */
export const updateRole = async (data: any) => {
    if (!data.id) {
        throw new Error('角色ID不能为空');
    }
    const options = {
        loadingMessage: '正在更新角色',
        successMessage: '角色更新成功'
    }
    if ('permissions' in data) {
        options['loadingMessage'] = '正在更新角色权限';
        options['successMessage'] = '角色权限更新成功';
    }
    return (await put('/api/v1/role', data, undefined, options)).data;
}

/**
 * 删除用户
 * @param id 用户ID
 * @returns 删除结果
 * @version 1.0.0
 */
export const deleteUser = async (id: number | undefined) => {
    if (!id) {
        message.error('请选择用户');
    }
    return (await del(`/api/v1/user`, { id }, {
        loadingMessage: '正在删除用户',
        successMessage: '用户删除成功'
    })).data;
}

export const getDepartmentTree = async (): Promise<Department[]> => {
    return (await get('/api/v1/departments', { as: 'tree' }, {
        loadingMessage: '正在加载部门列表',
        successMessage: '部门列表加载成功',
    })).data;
};


export const listUsers = async (searchParams: any): Promise<any> => {
    return (await get('/api/v1/users', searchParams, {
        loadingMessage: '正在加载用户列表',
        successMessage: '用户列表加载成功'
    })).data;
};

export const getUserById = async (id: number): Promise<any> => {
    return (await get(`/api/v1/user`, { id }, {
        loadingMessage: '正在加载用户信息',
        successMessage: '用户信息加载成功'
    })).data;
};

export const updateUser = async (data: any) => {
    const options = {
        loadingMessage: '正在更新用户信息',
        successMessage: '用户信息更新成功'
    };
    if ('roles' in data) {
        options['loadingMessage'] = '正在更新用户角色'
        options['successMessage'] = '用户角色更新成功'
    } else if ('status' in data) {
        options['loadingMessage'] = '正在更新用户状态'
        options['successMessage'] = '用户状态更新成功'
    } else if ('department' in data) {
        options['loadingMessage'] = '正在更新用户部门'
        options['successMessage'] = '用户部门更新成功'
    }
    return (await put('/api/v1/user', data, undefined, options)).data;
};

export const createUser = async (data: any) => {
    const res: any = await post('/api/v1/user', data, undefined, {
        loadingMessage: '正在创建用户',
        successMessage: '用户创建成功',
        headers: {
            [AppConfig.headers.useApiTip]: 'false'
        }
    });
    if (res.code !== '0') {
        throw new Error(res.desc || '创建用户失败');
    }
    return res.data;
};

/**
 * 创建部门
 * @param data 部门数据
 * @returns 部门数据
 * @version 1.0.0
 */
export const createDepartment = async (data: any) => {
    return (await post('/api/v1/department', data, undefined, {
        loadingMessage: '正在创建部门',
        successMessage: '部门创建成功'
    }));
};

/**
 * 修改部门
 * @param data 部门数据
 * @returns 部门数据
 * @version 1.0.0
 */
export const updateDepartment = async (data: any) => {
    return (await put('/api/v1/department', data, undefined, {
        loadingMessage: '正在修改部门',
        successMessage: '部门修改成功'
    }));
};

/**
 * 删除部门
 * @param id 部门ID
 * @returns 删除结果
 * @version 1.0.0
 */
export const deleteDepartment = async (id: number | undefined) => {
    if (!id) {
        message.error('部门ID不能为空');
        return;
    }
    return (await del(`/api/v1/department`, { id }, {
        loadingMessage: '正在删除部门',
        successMessage: '部门删除成功'
    })).data;
}

export const resetPassword = async (data: any) => {
    return (await put('/api/v1/user', data, undefined, {
        loadingMessage: '正在重置用户密码',
        successMessage: '用户密码重置成功'
    })).data;
};

export const logout = async (account: string, userId: number) => {
    return (await post('/api/v1/auth/logout', { account, userId }, undefined, {
        loadingMessage: '正在退出登录',
        successMessage: '退出登录成功'
    })).data;
};

export const refreshUserPermissions = async (roleCode: string) => {
    return (await post('/api/v1/users/refresh-permissions', { roleCode }, undefined, {
        loadingMessage: '正在刷新用户权限',
        successMessage: '用户权限刷新成功'
    })).data;
};